import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";

// A5: client-side trend bucketing thresholds.
const TREND_UP_THRESHOLD = 0.25;
const TREND_DOWN_THRESHOLD = -0.25;

// Dev-only sanity check: edge fn must ship these keys for the Studio prompt
// to interpolate. We warn (don't throw) once per session to surface drift
// between the prompt and pulse-conversation-token without affecting prod.
const REQUIRED_DYNAMIC_KEYS = [
  "org_name",
  "employee_handle",
  "recent_pulse_score",
  "recent_pulse_trend_7d",
  "voice_opt_in",
  "charles_display_name",
  "opening_message",
] as const;
let missingVarsWarned = false;

function warnIfMissingDynamicVars(dv: Record<string, unknown>): void {
  if (!import.meta.env.DEV) return;
  if (missingVarsWarned) return;
  const missing = REQUIRED_DYNAMIC_KEYS.filter((k) => {
    const v = dv[k];
    return v === undefined || v === null || v === "";
  });
  // recent_pulse_score legitimately may be null when there's no 24h pulse —
  // exclude it from the "missing" check (presence of the key is enough).
  const filtered = missing.filter(
    (k) => !(k === "recent_pulse_score" && k in dv),
  );
  if (filtered.length === 0) return;
  missingVarsWarned = true;
  // eslint-disable-next-line no-console
  console.warn(`[charles] missing dynamic_variables: ${filtered.join(", ")}`);
}

export type ConversationErrorCode =
  | "unauthorized"
  | "not_pulse_employee"
  | "no_active_membership"
  | "config_error"
  | "network_error"
  | "server_error"
  | "daily_limit"
  | "mic_denied";

export type AnchorBrief = {
  name: string;
  description: string | null;
  scale: string | null;
  target_score: number | null;
};

export type DynamicVariables = Record<string, unknown> & {
  voice_opt_in?: boolean;
};

export type PulseConversationState =
  | { status: "loading" }
  | { status: "error"; code: ConversationErrorCode; message?: string }
  | {
      status: "ready";
      conversationId: string;
      conversationToken: string;
      signedUrl: string | null;
      dynamicVariables: DynamicVariables;
      allowedOrigins: string[];
      /**
       * Re-mints token + conversation row + dynamic variables from the edge
       * function. Call this immediately before startSession so Charles always
       * sees the latest pulse/state/keypoints — including a pulse the user
       * just submitted seconds ago.
       */
      refresh: () => Promise<PulseConversationState>;
    };

function bucketTrend(delta: number | null): "up" | "down" | "flat" | null {
  if (delta === null || delta === undefined) return null;
  if (delta > TREND_UP_THRESHOLD) return "up";
  if (delta < TREND_DOWN_THRESHOLD) return "down";
  return "flat";
}

async function mintConversation(
  orgId: string,
  opts?: { siteId?: string; agentId?: string },
): Promise<PulseConversationState> {
  const body: Record<string, string> = {};
  if (opts?.siteId) body.site_id = opts.siteId;
  if (opts?.agentId) body.agent_id = opts.agentId;
  const { data, error } = await supabase.functions.invoke(
    "pulse-conversation-token",
    { body },
  );

  if (error) {
    const body = (error as any)?.context?.body;
    const code = body?.error as ConversationErrorCode | undefined;
    if (
      code === "unauthorized" ||
      code === "not_pulse_employee" ||
      code === "no_active_membership"
    ) {
      return { status: "error", code };
    }
    return { status: "error", code: "network_error", message: error.message };
  }

  const token = (data as any)?.token as string | undefined;
  const signedUrl = (data as any)?.signed_url as string | null | undefined;
  const dv = (data as any)?.dynamic_variables;
  if (!token || !dv) {
    return { status: "error", code: "server_error", message: "token_missing" };
  }

  warnIfMissingDynamicVars(dv as Record<string, unknown>);

  const channel: "chat" | "voice" = dv.voice_opt_in ? "voice" : "chat";
  const { data: conv, error: convErr } = await supabase.rpc(
    "start_conversation",
    { _org_id: orgId, _channel: channel },
  );
  if (convErr || !conv?.id) {
    const msg = convErr?.message ?? "no_conversation_id";
    const code: ConversationErrorCode = /daily_conversation_limit|daily conversation limit/i.test(msg)
      ? "daily_limit"
      : "server_error";
    return { status: "error", code, message: msg };
  }

  return {
    status: "ready",
    conversationId: conv.id as string,
    conversationToken: token,
    signedUrl: signedUrl ?? null,
    dynamicVariables: {
      ...(dv as Record<string, unknown>),
      recent_pulse_trend_7d: bucketTrend(dv.recent_pulse_trend_7d ?? null),
      voice_opt_in: !!dv.voice_opt_in,
    },
    allowedOrigins: [],
    refresh: async () => ({ status: "loading" }), // replaced below
  };
}

export function usePulseConversation(
  orgId: string | null,
  opts?: { siteId?: string; agentId?: string },
) {
  const [state, setState] = useState<PulseConversationState>({
    status: "loading",
  });
  const orgIdRef = useRef(orgId);
  orgIdRef.current = orgId;
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const refresh = useCallback(async (): Promise<PulseConversationState> => {
    const id = orgIdRef.current;
    if (!id) {
      const s: PulseConversationState = { status: "loading" };
      setState(s);
      return s;
    }
    setState({ status: "loading" });
    const next = await mintConversation(id, optsRef.current);
    const withRefresh: PulseConversationState =
      next.status === "ready" ? { ...next, refresh } : next;
    setState(withRefresh);
    return withRefresh;
  }, []);

  const siteId = opts?.siteId;
  const agentId = opts?.agentId;
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      const next = await mintConversation(orgId, { siteId, agentId });
      if (cancelled) return;
      setState(next.status === "ready" ? { ...next, refresh } : next);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, siteId, agentId, refresh]);

  return state;
}


